import { isEmpty, isNil, isNumber, partition } from 'lodash';
import { z } from 'zod';
import {
  ICohortRefFieldsFragment,
  Order,
} from '../../../utils/apollo/graphqlSchema';
import { formatNumberWithComma } from '../../../utils/formatText';
import {
  IParsedChartDataDataTable,
  IParsedDataTableSlice,
} from '../interfaces';
import { isOverallSliceKey, isSegmentSliceKey } from '../utils';
import { OVERALL_SLICE_KEY } from '../constants/utils';

type SliceMap = Array<Array<number | null>>;

/**
 * interleaveArrays: takes an array of arrays and interleaves them
 * @param arrays: an array of arrays of the same length, containing numbers
 * @returns an array of numbers, interleaved from the input arrays
 */
export const interleaveArrays = (arrays: SliceMap) => {
  const arrayLength = arrays
    .map((array) => array.length)
    .reduce((max, current) => {
      return max > current ? max : current;
    }, 0);

  const result = [];
  for (let i = 0; i < arrayLength; i++) {
    for (let array of arrays) {
      result.push(array[i]);
    }
  }
  return result;
};

// from the slices and their respective hints,
// generate an ordered list of segment values
const generateSegmentsFromHints = (chartData: IParsedChartDataDataTable) => {
  const uniqueSegments = chartData.slices.reduce((uniqueSet, slice) => {
    const segmentHint = slice.hint.find((hint) =>
      isSegmentSliceKey(hint.name.toLowerCase()),
    );
    if (segmentHint) {
      uniqueSet.add(segmentHint.value);
    }
    return uniqueSet;
  }, new Set());
  return Array.from(uniqueSegments) as string[];
};

// segments from builderInfo are ordered, use if it exists
const generateSegmentsFromCohortRefs = (
  cohortRefs: ICohortRefFieldsFragment[],
) => {
  return cohortRefs.map((cohortRef) => {
    return cohortRef.cohort.name;
  }) as string[];
};

export const generateSegments = (
  chartData: IParsedChartDataDataTable,
  cohortRefs?: ICohortRefFieldsFragment[],
): string[] => {
  if (cohortRefs?.length) {
    return generateSegmentsFromCohortRefs(cohortRefs);
  } else {
    return generateSegmentsFromHints(chartData);
  }
};

// a helper function to fill the slicemap based on the slice & segment index.
const fillSliceMap = (
  slice: IParsedDataTableSlice,
  sliceMap: SliceMap,
  segments: string[],
): SliceMap => {
  return segments.map((segment, index) => {
    // fill the slice map with the slice measures
    if (slice.name.includes(segment)) {
      return slice.measures;
    }

    // otherwise if the row exists in the slice map, return the row
    if (sliceMap[index]) {
      return sliceMap[index];
    }

    // if nothing exists in that row of the slice map, fill with an array of nulls.
    return new Array(slice.measures.length).fill(null);
  });
};

// reformats the slices into a Map of
// key: breakdown values string joined by '-'
// value: 2ds array of measure indices, broken down by segment
export const generateSlicesMap = (
  chartData: IParsedChartDataDataTable,
  segments: string[],
  hasComparison?: boolean,
) => {
  return chartData.slices.reduce((result, slice) => {
    // KBT-1268: this issue was produced when Kobol always return overall slice
    // to calculate current sum value for all returned slices.
    // But unfortunately, the overall slice should not included in DataTable
    // when there is any breakdown or segments.
    // Therefore, we need to skip the overall slice if overall is not the only slice
    // will talk to Kobol to see if Kobol can handle this case.
    if (slice.key === OVERALL_SLICE_KEY && chartData.slices.length > 1) {
      return result;
    }
    const slicePartition = partition(slice.hint, (hint) => {
      return !isSegmentSliceKey(hint.name.toLowerCase());
    });

    const fieldSlices = slicePartition[0];
    const fieldKey = JSON.stringify(
      fieldSlices.map((hint) => {
        return hint.value;
      }),
    );

    const current = result.get(fieldKey) ?? [];
    if (segments.length > 0) {
      let measures = slice.measures;

      if (!isNil(hasComparison)) {
        measures = slice.measures.filter(
          (colIndex) =>
            chartData.columns[colIndex].isComparison === hasComparison,
        );
      }

      const arr = fillSliceMap(
        {
          ...slice,
          measures,
        },
        current,
        segments,
      );
      result.set(fieldKey, arr);
    } else {
      current.push(slice.measures);
      result.set(fieldKey, current);
    }
    return result;
  }, new Map<string, SliceMap>());
};

// merges two slices maps by interleaving the previous and current values for each segment
const mergeSlicesMaps = (
  slicesMapPrevious: Map<string, SliceMap>,
  slicesMapCurrent: Map<string, SliceMap>,
) => {
  const mergedSlicesMap = new Map<string, SliceMap>();

  for (const [key, value] of slicesMapPrevious) {
    mergedSlicesMap.set(key, value);
  }

  for (const [key, previousValue] of slicesMapPrevious) {
    const currentValue = slicesMapCurrent.get(key) || [];

    // Merge the arrays by interleaving previous and current values for each segment
    const mergedValue = previousValue.reduce(
      (acc: SliceMap, prevSegment, index) => {
        const currSegment = currentValue[index] || [];
        acc.push(prevSegment);
        acc.push(currSegment);
        return acc;
      },
      [],
    );

    mergedSlicesMap.set(key, mergedValue);
  }

  return mergedSlicesMap;
};

// generates the body rows for the data table
export const generateDataTableRows = (
  chartData: IParsedChartDataDataTable,
  segments: string[],
  hasComparison?: boolean,
  showPercentDelta?: boolean,
) => {
  let slicesMap = null;

  // special case for comparison with segments
  // generate two maps, one for previous and one for current
  // then merge them by interleaving the values for each segment
  if (hasComparison && segments.length > 0) {
    const slicesMapPrevious = generateSlicesMap(chartData, segments, true);
    const slicesMapCurrent = generateSlicesMap(chartData, segments, false);
    slicesMap = mergeSlicesMaps(slicesMapPrevious, slicesMapCurrent);
  } else {
    slicesMap = generateSlicesMap(chartData, segments);
  }

  const tableRows = [];

  for (const [key, value] of slicesMap) {
    const keyFormat = z.array(z.string());
    const breakdownValues = keyFormat.parse(JSON.parse(key));
    const segmentValues = interleaveArrays(value);

    const values = segmentValues.map((value) =>
      !isNil(value) ? chartData.columns[value].values[0] : null,
    );

    // Calculate percent delta if showPercentDelta is true
    if (showPercentDelta) {
      for (let i = 0; i < values.length; i += 3) {
        const previous = values[i];
        const current = values[i + 1];

        if (isNumber(previous) && isNumber(current)) {
          const percentDelta = parseFloat(
            (((current - previous) / previous) * 100).toFixed(2),
          );
          values.splice(i + 2, 0, percentDelta); // Insert percent delta after current
        } else {
          values.splice(i + 2, 0, null); // Insert null if calculation is not possible
        }
      }
    }

    // if key is an empty string (no breakdown)
    // or if key is an overall slice key
    // push the segmented values directly
    // else push the breakdown values first
    if (
      isEmpty(key) ||
      (breakdownValues.length &&
        isOverallSliceKey(breakdownValues[0].toLowerCase()))
    ) {
      tableRows.push(values);
    } else {
      tableRows.push([...breakdownValues, ...values]);
    }
  }

  return tableRows;
};

// calculates the offset from the top of the data table
// to when the data rows actually starts
export const calculateHeaderRowsOffset = (
  hasHeader: boolean,
  hasBreakdown?: boolean,
  hasSegment?: boolean,
  hasComparison?: boolean,
) => {
  let offset = 0;

  if (hasHeader) {
    offset++;
  }

  if (hasBreakdown) {
    offset++;
  }

  if (hasSegment) {
    offset++;
  }

  if (hasComparison) {
    offset++;
  }

  return offset;
};

const getColumnNumericValues = (
  rows: Array<Array<string | number | null>>,
  columnIndex: number,
) => {
  const columnValues = rows
    .map((row) => row[columnIndex])
    .filter((value) => isNumber(value)) as number[];

  return columnValues;
};

// generates a sum of all the rows in a column
// to present at the top of the data table
// for simple measures.
export const getColumnSumValue = (
  rows: Array<Array<string | number | null>>,
  columnIndex: number,
) => {
  return rows.reduce((acc, row) => {
    const cell = row[columnIndex];
    if (Number(cell) > 0) {
      return acc + Number(cell);
    }
    return acc;
  }, 0);
};

export const getColumnAggregatedValue = (
  rows: Array<Array<string | number | null>>,
  columnIndex: number,
  functionKey: string,
): number | null => {
  if (functionKey === 'AVG') {
    const columnSum = getColumnSumValue(rows, columnIndex);
    return columnSum / rows.length;
  } else if (functionKey === 'MAX') {
    const columnValues = getColumnNumericValues(rows, columnIndex);
    return Math.max(...columnValues);
  } else if (functionKey === 'MIN') {
    const columnValues = getColumnNumericValues(rows, columnIndex);
    return Math.min(...columnValues);
  } else if (functionKey === 'MEDIAN') {
    const columnValues = getColumnNumericValues(rows, columnIndex);
    columnValues.sort((a, b) => a - b);
    if (columnValues.length > 0) {
      const mid = Math.floor(columnValues.length / 2);

      if (columnValues.length % 2 === 0) {
        return (columnValues[mid - 1] + columnValues[mid]) / 2;
      }
      return columnValues[mid];
    }

    return null;
  } else if (functionKey === 'SUM') {
    const columnSum = getColumnSumValue(rows, columnIndex);
    return columnSum;
  } else if (functionKey.startsWith('UNIQUE')) {
    // TODO: this is going to cause problems in the future, until we have a way to handle
    // specific customer function keys related to the UNIQUE function
    const columnSum = getColumnSumValue(rows, columnIndex);
    return columnSum;
  } else if (functionKey.startsWith('COUNT')) {
    const columnSum = getColumnSumValue(rows, columnIndex);
    return columnSum;
  }

  return null;
};

export const filterDataTableRows = ({
  rows,
  searchTerm,
  sortColumnIndex,
  sortDirection,
}: {
  rows: Array<Array<string | number | null>>;
  searchTerm?: string;
  sortColumnIndex?: number | null;
  sortDirection?: Order;
}) => {
  let result = [...rows];

  // filter by searchTerm
  if (searchTerm?.length) {
    result = filterRowsBySearchTerm(result, searchTerm);
  }

  // filter by sort
  if (!isNil(sortColumnIndex) && !isNil(sortDirection)) {
    result = sortDataTableRows(result, sortColumnIndex, sortDirection);
  }

  return result;
};

// search term filter for data table
const filterRowsBySearchTerm = (
  rows: Array<Array<string | number | null>>,
  searchTerm: string,
) => {
  return rows.filter((row) =>
    row.some(
      (cell) =>
        !isNil(cell) &&
        cell.toString().toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  );
};

// sort logic for data table rows.
const sortDataTableRows = (
  rows: Array<Array<string | number | null>>,
  sortColumnIndex: number,
  sortDirection: Order,
) => {
  rows.sort((a, b) => {
    const aValue = a[sortColumnIndex];
    const bValue = b[sortColumnIndex];

    // Check for null values
    if (aValue === null && bValue === null) {
      return 0; // Both are null, considered equal
    } else if (aValue === null) {
      return sortDirection === Order.Asc ? -1 : 1; // null is considered smaller
    } else if (bValue === null) {
      return sortDirection === Order.Asc ? 1 : -1; // null is considered smaller
    }

    // Existing comparison logic for numbers
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === Order.Asc ? aValue - bValue : bValue - aValue;
    }

    // Existing comparison logic for strings
    else if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === Order.Asc
        ? aValue.localeCompare(bValue, 'en', { numeric: true })
        : bValue.localeCompare(aValue, 'en', { numeric: true });
    }

    return 0;
  });

  return rows;
};

export const calculateMeasureIndex = (
  colIndex: number,
  segments: any[],
  hasComparison: boolean,
  numComparisonColumns: number,
) => {
  let index = colIndex;

  if (hasComparison) {
    index = Math.floor(colIndex / numComparisonColumns);
  }

  if (segments.length > 0) {
    index = Math.floor(index / segments.length);
  }

  return index;
};

export const renderPercentageText = (percentage: number) => {
  if (!isFinite(percentage) || isNil(percentage)) {
    return '-';
  }

  return formatNumberWithComma(`${percentage.toFixed(2)}%`);
};
